// ============================================================================
// THEME.JS - Central Bootstrap & Configuration
// ============================================================================
// Purpose: Single central location for sitewide initialization
// - Publish SA_CART_MODE for all features to use
// - Provide init-once wrapper to prevent duplicate initializations
// - Initialize global modules that are used everywhere

// ============================================================================
// 1. EXPOSE CART MODE (from Liquid)
// ============================================================================
// window.SA_CART_MODE is set in theme.liquid and available globally
// Features can consume this: if (window.SA_CART_MODE === 'drawer') { ... }

// ============================================================================
// 2. INIT ONCE HELPER - Prevent duplicate initializations
// ============================================================================

/**
 * Check if we're in a dev environment (for logging purposes)
 * Dev = localhost, 127.0.0.1, or Shopify designMode
 * @returns {boolean}
 */
function isDevEnvironment() {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    Shopify?.designMode === true
  );
}

/**
 * Dev-only logging utility
 * Only logs in dev environments to keep production console clean
 * @param {string} prefix - Log prefix (e.g., 'initOnce')
 * @param {string} message - Message to log
 */
function devLog(prefix, message) {
  if (isDevEnvironment()) {
    console.log(`[${prefix}] ${message}`);
  }
}

/**
 * Initialize a feature only once, even if called multiple times
 * Sets a marker on the element to prevent re-initialization
 * 
 * @param {HTMLElement} el - Element to mark as initialized
 * @param {string} key - Unique identifier for this initialization (e.g., 'sticky-atc')
 * @returns {boolean} - Returns true if this is the first init, false if already initialized
 * 
 * Usage (guard pattern):
 *   if (!initOnce(element, 'sticky-atc')) return;
 *   // ... initialization code runs only once
 * 
 * Usage (legacy callback pattern):
 *   initOnce(document, 'global-details', () => {
 *     initDetailsElements();
 *   });
 */
function initOnce(el, key, fn) {
  // Handle legacy 3-arg pattern: initOnce(key, fn, root)
  // where first arg is a string (key) and second is a function
  if (typeof el === 'string' && typeof key === 'function') {
    const legacyKey = el;
    const legacyFn = key;
    const legacyRoot = fn || document;

    // Use global tracker for legacy pattern
    if (!window._initOnceTracker) {
      window._initOnceTracker = new Set();
    }

    if (!window._initOnceTracker.has(legacyKey)) {
      window._initOnceTracker.add(legacyKey);
      devLog('initOnce', `Initializing: ${legacyKey}`);
      legacyFn();
      return true;
    }

    devLog('initOnce', `Already initialized: ${legacyKey}`);
    return false;
  }

  // New element-based pattern: initOnce(element, key) or initOnce(element, key, fn)
  if (!el || !(el instanceof HTMLElement)) {
    devLog('initOnce', `Invalid element for key: ${key}`);
    return false;
  }

  // Use data-sa-initialized attribute to track per-element
  const markerAttr = 'data-sa-initialized';
  const currentMarker = el.getAttribute?.(markerAttr);

  // Check if this key is already in the marker
  if (currentMarker) {
    const keys = currentMarker.split(' ');
    if (keys.includes(key)) {
      devLog('initOnce', `Already initialized: ${key}`);
      return false;
    }
    // Add new key to existing markers
    el.setAttribute(markerAttr, `${currentMarker} ${key}`);
  } else {
    el.setAttribute?.(markerAttr, key);
  }

  devLog('initOnce', `Initializing: ${key}`);

  // If callback provided, call it
  if (typeof fn === 'function') {
    fn();
  }

  return true;
}

// Expose utilities globally
window.initOnce = initOnce;
window.isDevEnvironment = isDevEnvironment;
window.devLog = devLog;

// ============================================================================
// 3. GLOBAL MODULES (from global.js)
// ============================================================================
/**
 * Initialize details elements (moved from global.js)
 * Sets up aria-expanded attributes and keyboard handlers
 */
function initDetailsElements() {
  document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

    if (summary.nextElementSibling.getAttribute('id')) {
      summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    }

    summary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (summary.closest('header-drawer, menu-drawer')) return;
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  });
}

/**
 * Initialize focus-visible polyfill (moved from global.js)
 * Detects browser support and applies fallback if needed
 */
function initFocusVisiblePolyfill() {
  try {
    document.querySelector(':focus-visible');
  } catch (e) {
    focusVisiblePolyfill();
  }
}

/**
 * Initialize global modules when DOM is ready
 */
function initGlobalModules() {
  // Initialize details/disclosure elements
  initOnce('global-details', () => {
    initDetailsElements();
  });

  // Initialize focus-visible polyfill
  initOnce('global-focus-visible', () => {
    initFocusVisiblePolyfill();
  });

  // Header/nav initialization (placeholder for future header features)
  // Currently header is initialized via Dawn's native header.js

  // Mobile menu initialization (placeholder for future menu features)
  // Currently menu is initialized via Dawn's native menu-drawer.js
}

// ============================================================================
// 4. DOM READY & BOOTSTRAP
// ============================================================================
/**
 * Main initialization function - runs when DOM is ready
 */
function initTheme() {
  // Publish cart mode for all features
  devLog('Theme', `Initialized with cart mode: ${window.SA_CART_MODE}`);

  // Initialize global modules
  initGlobalModules();

  // Custom event for other scripts to hook into
  document.dispatchEvent(new CustomEvent('theme:ready', {
    detail: {
      cartMode: window.SA_CART_MODE,
    },
  }));
}

// Run initialization when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTheme);
} else {
  initTheme();
}
